import { Component, computed, ElementRef, HostListener, OnInit, signal, ViewChild, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { TegelModule } from '@scania/tegel-angular-17';
import { CUSTOM_ELEMENTS_SCHEMA, inject } from '@angular/core';
import { HeaderService } from '../header/header.service';
import { AuthService } from '@/app/core/services/auth.service';
import { RoleEnum } from '@/app/consts/role.const';
import { AllAppLinks, AppLink, getNavItemsByRole } from '@/app/consts/app-links.config';
import { HasRoleDirective } from '@/app/directives/has-role.directive';
import { TranslateModule } from '@ngx-translate/core';
import { FullscreenService } from '@/app/core/services/fullscreen.service';
import { LanguageService } from '@/app/core/services/language.service';
import { RoleTranslatePipe } from '@/app/core/pipes/role-translate.pipe';
import { RoleSwitcherService } from '@/app/core/services/role-switcher.service';
@Component({
  selector: 'app-side-nav',
  standalone: true,
  imports: [CommonModule, RouterModule, TegelModule, HasRoleDirective, TranslateModule, RoleTranslatePipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss']
})
export class SideNavComponent implements OnInit {

  @ViewChild('collapseButton', { static: true }) collapseButton!:CustomEvent;
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private authService = inject(AuthService);
  protected headerService = inject(HeaderService);
  private fullscreenService = inject(FullscreenService);
  private languageService = inject(LanguageService);
  private roleSwitcherService = inject(RoleSwitcherService);
  menuNavCollapsed = this.headerService.menuNavCollapsed;

  // Computed: mobileNavOpen forces collapsed=false; otherwise use collapsed() signal
  isSideMenuCollapsed = computed(() => this.mobileNavOpen() ? false : this.menuNavCollapsed());

  // User profile properties
  userName = 'John Doe';
  placeOfWork = 'Scania';

  // Mobile menu state
  mobileNavOpen = this.headerService.mobileNavOpen;

  // 动态计算导航项，基于当前选择的角色
  navItems = computed(() => {
    const selectedRole = this.authService.selectedRole();
    if (!selectedRole) {
      return this.filterNavItems(AllAppLinks);
    }
    
    // 使用角色过滤导航项
    const roleBasedItems = getNavItemsByRole(selectedRole as RoleEnum);
    return this.filterNavItems(roleBasedItems);
  });

  // Role related computed signals
  hasMultipleRoles = this.roleSwitcherService.hasMultipleRoles;
  availableRoles = this.roleSwitcherService.availableRoles;
  getCurrentRole = this.roleSwitcherService.selectedRole;

  constructor() {
    // Ensure side menu is visible
    if (typeof document !== 'undefined') {
      setTimeout(() => {
        const sideMenu = document.querySelector('tds-side-menu');
        if (sideMenu) {
          sideMenu.setAttribute('style', 'display: block; height: 100%;');
        }
      });
    }
  }
  ngOnInit(): void {
    this.collapseSideMenu();
  }

  private collapseSideMenu(){
    const w = window.innerWidth;
   if(w>1200){
      // Desktop: restore user's collapse preference, ensure mobile nav is closed
      this.headerService.setMenuNavCollapsed(false);
      // const menuNavCollapsed = this.headerService.menuNavCollapsed();
      // this.collapsed.set(menuNavCollapsed);
      // this.headerService.setMenuNavCollapsed(menuNavCollapsed);
      this.headerService.closeMobileSideMenu();
    }else  if(w >= 992 && w <=1200){
      this.headerService.setMenuNavCollapsed(true);
      this.headerService.closeMobileSideMenu();
    }else{
      this.headerService.setMenuNavCollapsed(true);
    } 
  }

  private filterNavItems(links: AppLink[]): AppLink[] {
    return links
      .filter(link => !link.isHideInSideNav)
      .map(link => {
        const filteredChildren = link.children ? this.filterNavItems(link.children) : undefined;
        
        // Single-child collapse: if only 1 child remains, collapse to regular menu item
        if (filteredChildren && filteredChildren.length === 1) {
          const onlyChild = filteredChildren[0];
          return {
            ...link,
            label: link.translationKey, // Use translation key as label for pipe
            route: onlyChild.route, // Use child's route
            children: undefined, // Remove children to render as regular item
          };
        }
        
        return {
          ...link,
          label: link.translationKey, // Use translation key as label for pipe
          children: filteredChildren
        };
      });
  }

  navigateTo(route: string): void {
    this.router.navigate([route]);
    this.closeMobileSideMenu();
  }

  closeMobileSideMenu(): void {
    this.headerService.closeMobileSideMenu();
  }

  toggleMobileSideMenu(): void {
    this.headerService.toggleMobileSideMenu();
  }

  isActive(route: string | undefined): boolean {
    return this.router.url.includes(`/${route}`);
  }

  isActiveChild(navItem: AppLink) {
    return navItem.children?.some(nav => this.router.url === `/${nav.route}`);
  }

  onSubRoute(route: string): boolean {
    return this.router.url.startsWith(route);
  }

  languageOptions = [
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Español' },
    { code: 'fr', label: 'Français' }
  ];

  currentLanguage(): string {
    return this.languageService.currentLanguage();
  }

  setLanguage(code: string): void {
    this.languageService.setLanguage(code as any);
  }

  onLanguageChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.setLanguage(target.value);
  }

  toggleLanguage(): void {
    const next = this.currentLanguage() === 'en' ? 'zh' : 'en';
    this.setLanguage(next);
  }

  isMobileOrTablet(): boolean {
    return window.innerWidth < 992;
  }

  toggleFullscreen() {
    this.fullscreenService.toggleFullscreen();
  }

  tdsCollapseEvent(event: any){
    // console.log("menu.....", event.detail?.collapsed);
    // this.collapsed.set(event.detail?.collapsed);
    this.headerService.setMenuNavCollapsed(event.detail?.collapsed);
    // If collapsing, close mobile nav to enforce mutual exclusion
    if (event.detail?.collapsed) {
      this.headerService.closeMobileSideMenu();
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
   this.collapseSideMenu();
  }

  onRoleChange = this.roleSwitcherService.onRoleChange;

  getCustomSvg(item: AppLink): string | null {
    return item.customSvg || null;
  }

  getNavItemChildId(item: AppLink): string {
    return `nav-child-${item.route}`;
  }
}